// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/translationService.ts
// 🔧 PURPOSE: Translation service using MyMemory API
// ============================================

const MYMEMORY_API = 'https://api.mymemory.translated.net/get';
const MYMEMORY_EMAIL = 'nonnavittoriaapartments@gmail.com';

export const SUPPORTED_TRANSLATION_LANGS = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'pl', label: 'Polski' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' },
] as const;

export type TranslationLangCode = typeof SUPPORTED_TRANSLATION_LANGS[number]['code'];

const STORAGE_KEY = 'nva_chat_translation_lang';

// In-memory cache: key = `${messageId}_${field}_${targetLang}`, value = translated text
const translationCache = new Map<string, string>();

/**
 * Get the saved translation language preference from localStorage.
 * Returns null if no preference is saved.
 */
export function getSavedTranslationLang(): TranslationLangCode | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_TRANSLATION_LANGS.some(l => l.code === saved)) {
      return saved as TranslationLangCode;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

/**
 * Save the translation language preference to localStorage.
 */
export function saveTranslationLang(lang: TranslationLangCode | null): void {
  try {
    if (lang) {
      localStorage.setItem(STORAGE_KEY, lang);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage not available
  }
}

/**
 * Detect the best default translation language from the browser.
 * Returns a supported language code, or null if the browser language
 * matches a common source language (it/en) or is not supported.
 */
export function detectBrowserLang(): TranslationLangCode | null {
  try {
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    if (browserLang && SUPPORTED_TRANSLATION_LANGS.some(l => l.code === browserLang)) {
      return browserLang as TranslationLangCode;
    }
  } catch {
    // navigator not available
  }
  return null;
}

/**
 * Get the initial translation language: saved preference > browser detection.
 */
export function getInitialTranslationLang(): TranslationLangCode | null {
  return getSavedTranslationLang() ?? detectBrowserLang();
}

/**
 * Translate a single text string using MyMemory API.
 * Uses autodetect for the source language.
 * Returns the original text if translation fails or if the text is empty.
 */
async function translateText(text: string, targetLang: string): Promise<string> {
  if (!text || !text.trim()) return text;

  const url = new URL(MYMEMORY_API);
  url.searchParams.set('q', text);
  url.searchParams.set('langpair', `autodetect|${targetLang}`);
  url.searchParams.set('de', MYMEMORY_EMAIL);

  try {
    const response = await fetch(url.toString());
    if (!response.ok) return text;

    const data = await response.json();

    if (data?.responseStatus === 200 && data?.responseData?.translatedText) {
      const translated = data.responseData.translatedText;
      // MyMemory returns the original text uppercased if it can't translate
      // or if source and target are the same language
      if (translated.toUpperCase() === text.toUpperCase() && translated === translated.toUpperCase()) {
        return text; // Likely same language or failed detection
      }
      return translated;
    }

    return text;
  } catch {
    // Network error - return original text silently
    return text;
  }
}

export interface TranslatedMessageFields {
  message: string;
  subject?: string;
  previewText?: string;
}

/**
 * Translate the translatable fields of a chat message.
 * Uses in-memory cache to avoid re-translating.
 * Only translates: message, subject, previewText (NOT htmlContent).
 */
export async function translateMessageFields(
  messageId: string,
  fields: TranslatedMessageFields,
  targetLang: string
): Promise<TranslatedMessageFields> {
  const result: TranslatedMessageFields = { message: fields.message };

  // Translate message (or previewText if available)
  const textToTranslate = fields.previewText || fields.message;
  const textCacheKey = `${messageId}_text_${targetLang}`;

  if (translationCache.has(textCacheKey)) {
    const cached = translationCache.get(textCacheKey)!;
    if (fields.previewText) {
      result.previewText = cached;
    } else {
      result.message = cached;
    }
  } else {
    const translated = await translateText(textToTranslate, targetLang);
    translationCache.set(textCacheKey, translated);
    if (fields.previewText) {
      result.previewText = translated;
    } else {
      result.message = translated;
    }
  }

  // Translate subject if present
  if (fields.subject) {
    const subjectCacheKey = `${messageId}_subject_${targetLang}`;
    if (translationCache.has(subjectCacheKey)) {
      result.subject = translationCache.get(subjectCacheKey)!;
    } else {
      const translatedSubject = await translateText(fields.subject, targetLang);
      translationCache.set(subjectCacheKey, translatedSubject);
      result.subject = translatedSubject;
    }
  }

  return result;
}

/**
 * Check if a translation is cached for a given message and language.
 */
export function isTranslationCached(messageId: string, targetLang: string): boolean {
  return translationCache.has(`${messageId}_text_${targetLang}`);
}

/**
 * Clear the entire translation cache.
 */
export function clearTranslationCache(): void {
  translationCache.clear();
}
